import { useEffect, useMemo, useState } from "react";
import { apiClient, ApiError } from "../api/client.js";
import { pollInboxUntilDone } from "./asset-history-tab.js";
import { formatBytes } from "./asset-helpers.js";
import { RestoreDiffPreview } from "./restore-diff-preview.js";
import { RestoreTargetPicker, suggestRestoreTarget } from "./restore-target-picker.js";

// 3.B.3.2: Folder-restore-modal. Käyttäjä klikkaa "Palauta kansio" /historia-
// näkymästä → modaali avautuu. Kerää sourcePath, targetPath, mode → POST
// inbox snapshot-restore → poll status → näyttää "Avaa Finderissa" -napin.

export type FolderRestoreModalProps = {
  snapshot: {
    id: string;
    sourcePath: string;
    startTime: string;
    fileCount: number;
    totalSize: number;
  };
  machineId: string;
  /** Initialisoitu sourcePath — default = snapshot.sourcePath (koko snapshot). */
  initialSourcePath?: string;
  /** 3.B.3.4: vertailupiste diff-preview:lle (uusin snapshot). Jos puuttuu, preview piiloutuu. */
  baselineSnapshotId?: string;
  onClose: () => void;
};

const RESTORE_TIMEOUT_MS = 60 * 60_000;
const REVEAL_TIMEOUT_MS = 60_000;

export function FolderRestoreModal({
  snapshot,
  machineId,
  initialSourcePath,
  baselineSnapshotId,
  onClose,
}: FolderRestoreModalProps) {
  const initialTarget = useMemo(() => suggestRestoreTarget(), []);
  const [sourcePath, setSourcePath] = useState(initialSourcePath ?? snapshot.sourcePath);
  const [target, setTarget] = useState(initialTarget);
  const [mode, setMode] = useState<"files-only" | "with-permissions" | "with-metadata">(
    "with-permissions"
  );
  const [status, setStatus] = useState<
    "idle" | "submitting" | "running" | "completed" | "failed" | "cancelled"
  >("idle");
  const [error, setError] = useState<string | null>(null);
  const [restoredPath, setRestoredPath] = useState<string | null>(null);
  const [revealStatus, setRevealStatus] = useState<"idle" | "opening" | "error">("idle");

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && status === "idle") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, status]);

  async function startRestore() {
    setStatus("submitting");
    setError(null);
    try {
      const created = await apiClient.postInboxRequest(machineId, {
        type: "snapshot-restore",
        request: {
          snapshotId: snapshot.id,
          sourcePath,
          targetPath: target,
          mode,
        },
      });
      setStatus("running");
      const result = await pollInboxUntilDone(created.id, RESTORE_TIMEOUT_MS);
      if (result.status === "completed") {
        const r = result.result as { restoredPath?: string } | null;
        setRestoredPath(r?.restoredPath ?? target);
        setStatus("completed");
      } else if (result.status === "cancelled") {
        setStatus("cancelled");
      } else {
        setStatus("failed");
        setError(result.error ?? `Restore ${result.status}`);
      }
    } catch (err) {
      setStatus("failed");
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err)
      );
    }
  }

  async function revealInFinder() {
    if (!restoredPath) return;
    setRevealStatus("opening");
    try {
      const created = await apiClient.postInboxRequest(machineId, {
        type: "reveal-in-finder",
        request: { path: restoredPath },
      });
      const result = await pollInboxUntilDone(created.id, REVEAL_TIMEOUT_MS);
      if (result.status === "completed") setRevealStatus("idle");
      else setRevealStatus("error");
    } catch {
      setRevealStatus("error");
    }
  }

  const busy = status === "submitting" || status === "running";

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 px-4 py-10"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="w-full max-w-2xl rounded-lg border border-slate-800 bg-slate-950 p-6 shadow-2xl">
        <header className="mb-4 flex items-start justify-between gap-4">
          <h3 className="text-lg font-semibold tracking-tight text-slate-100">
            Palauta snapshot:ista
          </h3>
          {!busy && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Sulje"
              className="rounded-md px-2 py-1 text-slate-400 hover:bg-slate-900 hover:text-slate-100"
            >
              ✕
            </button>
          )}
        </header>

        <div className="space-y-4">
          <div className="rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm">
            <p className="text-slate-300">
              {new Date(snapshot.startTime).toLocaleString("fi-FI")}
            </p>
            <p className="text-xs text-slate-500">
              {snapshot.fileCount.toLocaleString("fi-FI")} tiedostoa ·{" "}
              {formatBytes(snapshot.totalSize)} · root: {snapshot.sourcePath}
            </p>
          </div>

          <div className="space-y-1">
            <label className="block text-xs font-medium uppercase tracking-wide text-slate-400">
              Lähdepolku snapshot:issa
            </label>
            <input
              type="text"
              value={sourcePath}
              onChange={(e) => setSourcePath(e.target.value)}
              disabled={busy || status === "completed"}
              className="w-full rounded bg-slate-800 px-3 py-2 text-sm text-slate-100 disabled:opacity-50"
            />
            <p className="text-xs text-slate-500">
              Voi olla snapshot:in juuri tai mikä tahansa sen alipolku.
            </p>
          </div>

          <RestoreTargetPicker
            value={target}
            onChange={setTarget}
            disabled={busy || status === "completed"}
            machineId={machineId}
          />

          {baselineSnapshotId && status === "idle" && (
            <div className="space-y-1">
              <h4 className="block text-xs font-medium uppercase tracking-wide text-slate-400">
                Diff-esikatselu
              </h4>
              <RestoreDiffPreview
                machineId={machineId}
                snapshotId={snapshot.id}
                baselineSnapshotId={baselineSnapshotId}
                sourcePathFilter={sourcePath !== snapshot.sourcePath ? sourcePath : null}
              />
            </div>
          )}

          <fieldset className="space-y-1">
            <legend className="block text-xs font-medium uppercase tracking-wide text-slate-400">
              Mitä palautetaan
            </legend>
            <div className="space-y-1 text-sm text-slate-300">
              {[
                { v: "files-only" as const, l: "Vain tiedostot" },
                { v: "with-permissions" as const, l: "Tiedostot + permissionit + aikaleimat" },
                { v: "with-metadata" as const, l: "Kaikki metadata (owner-tiedot)" },
              ].map((opt) => (
                <label key={opt.v} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="folder-restore-mode"
                    value={opt.v}
                    checked={mode === opt.v}
                    onChange={() => setMode(opt.v)}
                    disabled={busy || status === "completed"}
                  />
                  <span>{opt.l}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="flex justify-end gap-2">
            {status === "idle" && (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded bg-slate-700 px-4 py-2 text-sm font-medium text-slate-100 hover:bg-slate-600"
                >
                  Peruuta
                </button>
                <button
                  type="button"
                  onClick={() => void startRestore()}
                  disabled={target.trim().length === 0 || sourcePath.trim().length === 0}
                  className="rounded bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-600 disabled:opacity-50"
                >
                  Palauta nyt
                </button>
              </>
            )}
            {busy && (
              <p className="text-sm text-slate-400">
                {status === "submitting"
                  ? "Lähetetään pyyntö daemonille…"
                  : "Kopia palauttaa tiedostoja. Iso restore voi kestää useita minuutteja."}
              </p>
            )}
          </div>

          {status === "completed" && (
            <div className="space-y-2 rounded-md border border-emerald-800 bg-emerald-950/40 px-3 py-2 text-sm">
              <p className="text-emerald-200">Palautettu polkuun: {restoredPath ?? target}</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => void revealInFinder()}
                  disabled={revealStatus === "opening"}
                  className="rounded bg-emerald-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
                >
                  {revealStatus === "opening" ? "Avataan…" : "Avaa Finderissa"}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded bg-slate-700 px-3 py-1.5 text-xs font-medium text-slate-100 hover:bg-slate-600"
                >
                  Sulje
                </button>
              </div>
              {revealStatus === "error" && (
                <p className="text-xs text-yellow-300">
                  Finder-avaus epäonnistui — avaa polku manuaalisesti.
                </p>
              )}
            </div>
          )}
          {status === "cancelled" && (
            <p className="text-sm text-yellow-300">Restore peruutettu.</p>
          )}
          {status === "failed" && error && (
            <p className="rounded-md border border-red-700 bg-red-950/40 px-3 py-2 text-sm text-red-200">
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
